import { observer } from "mobx-react";
import React from "react";
import { FilterKey } from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import {
    CallDirectionsChips,
    CallDurationChip,
    MessageCountChip,
    ClassifierChips,
    DefaultAppliedFilterChip,
    ExtendedMetadataGroupedAppliedFilterChip,
    EddyEffectChip,
    EvaluationTypesChips,
    FavoriteConversationsChip,
    HierarchiesChips,
    HipaaComplianceChip,
    MediaTypesChips,
    SafetyEventChip,
    SentimentChip,
    ThemesChips,
    TopicsChips,
    PrimaryTopicsChip,
    PrimaryDriversChip,
    OutlineChips,
} from ".";
import { AgentChips } from "../Agent/AgentChips";
import { AgentManagerChips } from "../AgentManager/AgentManagerChips";
import { ContactTypesChips } from "../ContactTypes/ContactTypesChips";
import { DateReferenceChip } from "../DateReference/DateReferenceChip";
import { IAppliedFilterChipComponent } from "./IAppliedFilterChipComponent";
import { PatientJourneyStagesChips } from "../PatientJourney/PatientJourneyStagesChips";
import { LanguageChips } from "../Language/LanguageChips";
import { ModelResultsChips } from "../ModelResults/ModelResultsChips";
import { ModuleScoresChips } from "../ModuleScores/ModuleScoresChips";
import { QuestionResultChip } from "../QuestionResult/QuestionResultChip";
import { ConversationLabelingChip } from "../ConversationLabeling/ConversationLabelingChip";
import { WordsAndPhrasesChip } from "../WordsPhrases/WordsAndPhrasesChip";
import { getGroupedExtendedMetadataValue } from "utils/extendedMetadata";
import { useChipFiltersStore } from "./chipUtils";
import { DateRangeChip } from "../DateRange/DateRangeChip";

const filterKeyToChip: Record<
    FilterKey,
    React.FunctionComponent<IAppliedFilterChipComponent> | undefined
> = {
    callDuration: CallDurationChip,
    minCallDuration: undefined,
    maxCallDuration: undefined,
    messageCount: MessageCountChip,
    minMessageCount: undefined,
    maxMessageCount: undefined,
    startDate: undefined,
    endDate: undefined,
    dateRange: DateRangeChip,
    dateReferenceOption: DateReferenceChip,
    dateRangeLabel: undefined,
    evaluationTypes: EvaluationTypesChips,
    hierarchyIds: HierarchiesChips,
    selectedCategories: undefined,
    selectedSubcategories: undefined,
    agentIds: AgentChips,
    agentManagerIds: AgentManagerChips,
    mediaTypes: MediaTypesChips,
    callDirections: CallDirectionsChips,
    clientCallId: DefaultAppliedFilterChip,
    meta1: DefaultAppliedFilterChip,
    meta2: DefaultAppliedFilterChip,
    meta3: DefaultAppliedFilterChip,
    meta4: DefaultAppliedFilterChip,
    meta5: DefaultAppliedFilterChip,
    extendedMetadata: DefaultAppliedFilterChip,
    extendedMetadataGrouped: ExtendedMetadataGroupedAppliedFilterChip,
    beginningSentiment: SentimentChip,
    endingSentiment: SentimentChip,
    eddyEffectStatus: EddyEffectChip,
    safetyEvent: SafetyEventChip,
    hipaaCompliance: HipaaComplianceChip,
    topics: TopicsChips,
    themes: ThemesChips,
    primaryTopics: PrimaryTopicsChip,
    drivers: PrimaryDriversChip,
    containsTopics: undefined,
    containsThemes: undefined,
    containsPrimaryTopics: undefined,
    containsDrivers: undefined,
    wordsAndPhrasesSearchString: WordsAndPhrasesChip,
    wordsAndPhrasesItems: undefined,
    wordsAndPhrasesIsInputStandard: undefined,
    rbcFilterItems: ClassifierChips,
    outlineSegmentFilterItems: OutlineChips,
    contactTypes: ContactTypesChips,
    patientJourneyStages: PatientJourneyStagesChips,
    favoriteConversations: FavoriteConversationsChip,
    containsDataLabels: undefined,
    containsResearchLabels: undefined,
    dataLabels: ConversationLabelingChip,
    modelResults: ModelResultsChips,
    moduleScores: ModuleScoresChips,
    questionResults: QuestionResultChip,
    embeddedTranscriptionQuery: DefaultAppliedFilterChip,
    embeddedTranscriptionQueryThreshold: undefined,
    languages: LanguageChips,
};

export const FilterChip = observer(function (
    props: IAppliedFilterChipComponent,
) {
    const applicationFiltersStore = useChipFiltersStore(props);

    let FilterChipComponent = filterKeyToChip[props.item.filterKey];

    const isExtendedeMetaItem =
        props.item.filterKey.startsWith("extendedMetadata");
    if (isExtendedeMetaItem) {
        const groupedValue = getGroupedExtendedMetadataValue(
            applicationFiltersStore,
            props.item.filterKey,
        );
        FilterChipComponent = groupedValue
            ? filterKeyToChip["extendedMetadataGrouped"]
            : filterKeyToChip["extendedMetadata"];
    }

    if (!FilterChipComponent) {
        console.error(
            `Invalid or unsupported filter key: ${props.item.filterKey}. Nothing rendered.`,
        );
        return null;
    }

    return <FilterChipComponent {...props} />;
});
